/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App, Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { applyFamilyApiError } from '@sportninja/common/features/profile/family/applyFamilyApiError'
import { FAMILY_SUCCESS_MESSAGES } from '@sportninja/common/features/profile/family/constants'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { useMePlayerProfile } from '../../player/setupModal/hooks/useMePlayerProfile'
import { useInviteFamilyContact } from '../../hooks/useInviteFamilyContact'
import { toFamilyContactPlayer } from './toFamilyContactPlayer'
import FamilyContactForm, {
  type FamilyContactFormValues,
} from './FamilyContactForm'

const EMPTY_FORM: FamilyContactFormValues = {
  nameFirst: '',
  nameLast: '',
  email: '',
  phone: '',
  relationship: null,
  isPrimary: true,
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  // Sits ABOVE the Family Management modal (10001/10000) it opens on top of, so
  // that modal is masked behind this one instead of bleeding through.
  wrapper: {
    zIndex: 10003,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10002,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

interface Props {
  isOpen: boolean
  onClose: () => void
  playerId: string | null
}

const InviteContactModal: React.FC<Props> = ({ isOpen, onClose, playerId }) => {
  const { message } = App.useApp()
  const inviteMutation = useInviteFamilyContact()
  // Fetch the selected player from the self-scoped endpoint so the card shows
  // the real player avatar + name (not the mocked family-list player).
  const { data: playerData } = useMePlayerProfile(playerId)
  const cardPlayer = toFamilyContactPlayer(playerData)
  const [values, setValues] = useState<FamilyContactFormValues>(EMPTY_FORM)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setValues(EMPTY_FORM)
      setFieldErrors({})
    }
  }, [isOpen])

  const handleConfirm = async () => {
    if (!playerId) {
      return
    }
    try {
      await inviteMutation.mutateAsync({
        playerId,
        name_first: values.nameFirst.trim(),
        name_last: values.nameLast.trim(),
        email: values.email.trim(),
        phone_number: values.phone || null,
        relationship_type_id: values.relationship,
        is_primary: values.isPrimary,
      })
      message.success(formatSuccessToast(FAMILY_SUCCESS_MESSAGES.invite))
      setValues(EMPTY_FORM)
      onClose()
    } catch (err) {
      applyFamilyApiError(err, { setFieldErrors, notifyError: message.error })
    }
  }

  const handleFormChange = (next: FamilyContactFormValues) => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev
      }
      const cleared = { ...prev }
      ;(Object.keys(next) as (keyof FamilyContactFormValues)[]).forEach((k) => {
        if (next[k] !== values[k] && k in cleared) {
          delete cleared[k as string]
        }
      })
      return cleared
    })
    setValues(next)
  }

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const header = (
    <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
      {capitalizeWords('Invite Family Member')}
    </Text>
  )

  const footer = (
    <div css={footerStyle}>
      <Button
        text='Cancel'
        onClick={onClose}
        variant='secondary'
        disabled={inviteMutation.isPending}
      />
      <Button
        text='Confirm'
        onClick={handleConfirm}
        variant='primary'
        isLoading={inviteMutation.isPending}
        disabled={inviteMutation.isPending}
      />
    </div>
  )

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        max-height: 90vh;
        margin: 24px auto;
        padding: 0;

        .ant-modal-content {
          max-height: 90vh;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
      `}
      width={600}
      closeIcon={closeIcon}
      title={header}
      footer={footer}
      open={isOpen}
      onCancel={onClose}
      centered
      styles={modalStyles}
      maskClosable={false}
    >
      <FamilyContactForm
        values={values}
        onChange={handleFormChange}
        player={cardPlayer}
        fieldErrors={fieldErrors}
      />
    </Modal>
  )
}

export default InviteContactModal

// ─── Styles ────────────────────────────────────────────────────────────────

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 50%;
  margin-left: auto;
`
